import { formatDateTime } from '@if/core/utils'
import {
  Alert,
  Box,
  List,
  ListSubheader,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useCurrentLang, useFeatureSwitches } from '@src/data/selectors/app-config-selectors'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FeaturesSwitch from './FeaturesSwitch'

const STALE_THRESHOLD_MS = 21 * 24 * 60 * 60 * 1000

type FilterStatus = 'all' | 'active' | 'inactive' | 'stale'

const FeaturesSwitchForm = () => {
  const featureSwitchInfo = useFeatureSwitches()!
  const featuresSwitches = featureSwitchInfo.featureSwitches
  const { t } = useTranslation()
  const lang = useCurrentLang()

  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput])
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success')
  const [snackbarMessage, setSnackbarMessage] = useState('')

  const sortedAndFilteredFeaturesSwitches = useMemo(() => {
    const filtered = featuresSwitches.filter((fs) => {
      const matchesSearch =
        fs.nameEn.toLowerCase().includes(searchQuery.toLowerCase()) ||
        fs.nameFr.toLowerCase().includes(searchQuery.toLowerCase()) ||
        fs.id.toLowerCase().includes(searchQuery.toLowerCase())

      const isStale =
        fs.activatedOn === null ||
        Date.now() - new Date(fs.activatedOn).getTime() > STALE_THRESHOLD_MS

      const matchesFilter =
        filterStatus === 'all' ||
        (filterStatus === 'active' && fs.isActivated) ||
        (filterStatus === 'inactive' && !fs.isActivated) ||
        (filterStatus === 'stale' && isStale)

      return matchesSearch && matchesFilter
    })
    const nameKey = lang === 'fr' ? 'nameFr' : 'nameEn'
    return filtered.sort((a, b) => {
      if (a.isActivated !== b.isActivated) return a.isActivated ? -1 : 1
      return a[nameKey].localeCompare(b[nameKey])
    })
  }, [featuresSwitches, searchQuery, filterStatus, lang])

  const handleUpdateResult = (success: boolean) => {
    if (success) {
      setSnackbarSeverity('success')
      setSnackbarMessage(
        `${t('featureSwitch.updatedSuccessfully')}. ${t('featureSwitch.nextActivationOn')} ${formatDateTime(featureSwitchInfo.nextRefreshDate)}`,
      )
    } else {
      setSnackbarSeverity('error')
      setSnackbarMessage(t('featureSwitch.updateFailed'))
    }
    setSnackbarOpen(true)
  }

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: FilterStatus | null) => {
    if (newFilter !== null) setFilterStatus(newFilter)
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <List
          sx={{ minWidth: 350, maxWidth: '100%' }}
          subheader={
            <ListSubheader component="div" id="nested-list-subheader">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('featureSwitch.featureSwitchPanel')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('featureSwitch.nextActivationOn')} {formatDateTime(featureSwitchInfo.nextRefreshDate)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={t('featureSwitch.searchPlaceholder')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <ToggleButtonGroup
                  value={filterStatus}
                  exclusive
                  onChange={handleFilterChange}
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  <ToggleButton value="all">{t('featureSwitch.all')}</ToggleButton>
                  <ToggleButton value="active" color="success">
                    {t('featureSwitch.active')}
                  </ToggleButton>
                  <ToggleButton value="inactive">{t('featureSwitch.inactive')}</ToggleButton>
                  <ToggleButton value="stale" color="warning">
                    {t('featureSwitch.stale')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </ListSubheader>
          }
        >
          {sortedAndFilteredFeaturesSwitches.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', marginTop: 2 }}>
              {t('featureSwitch.noFeatureAvailable')}
            </Typography>
          ) : (
            sortedAndFilteredFeaturesSwitches.map((featureSwitch) => (
              <FeaturesSwitch
                key={featureSwitch.id}
                featureSwitch={featureSwitch}
                onUpdateResult={handleUpdateResult}
              />
            ))
          )}
        </List>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default FeaturesSwitchForm
